import { useInfiniteQuery } from '@northladder/services';

import { INBOUND_API } from '../../../inboundAPIClient';
import { IBulkScanInventoriesPayload } from '../../../types';

/**
 * This is to track the bulk scan inventories from the backend.
 */
export const BULK_SCAN_INVENTORIES_KEY = 'bulk-scan-inventories-query';

interface IBulkScanInventoriesQuery {
  payload: IBulkScanInventoriesPayload;
}

/**
 * This hook fetches the bulk scan inventories list.
 */
export const useBulkScanInventoriesQuery = ({
  payload,
}: IBulkScanInventoriesQuery) => {
  const { inventoryTransactionId, limit, filter } = payload;

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      BULK_SCAN_INVENTORIES_KEY,
      inventoryTransactionId,
      limit,
      filter,
    ],
    initialPageParam: 1,
    networkMode: 'always',
    /**
     * This is to avoid the cache of the query, as an inbound can have multiple
     * bulk scan inventories.
     */
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    queryFn: ({ pageParam = 1 }) =>
      INBOUND_API.fetchBulkScanInventories({ ...payload, page: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const currentCount = allPages.length * limit;

      return currentCount < lastPage.total ? nextPage : undefined;
    },
  });

  return {
    bulkScanInventoriesData: data,
    bulkScanInventoriesError: error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    refetchBulkScanInventories: refetch,
  };
};
